








import { Component, Prop, Watch, Provide, Vue } from "vue-property-decorator";
import { getMin, getMax, generateUuid } from "@/helpers/MathsHelpers";
import * as d3 from "d3";
import { Category, Subcategory } from "@/services/CategoriesDataService";

export interface LineGraphPlotPoint {
  date: number;
  value: number;
}

export interface LineGraphSection {
  id: string;
  category: Category | Subcategory | null;
  color: string;
  title: string;
}

export interface LineGraphData extends LineGraphSection {
  data: LineGraphPlotPoint[];
}

export interface LineGraphOptions {
  min: number;
  max: number;
  showBeforeMin: boolean;
  showBeyondMax: boolean;
  beforeMinStyle: any;
  beyondMaxStyle: any;
}

@Component
export default class LineGraph extends Vue {
  @Prop({
    default() {
      return [];
    }
  })
  private data!: LineGraphData[];

  @Prop({ default: "" })
  private title?: string;

  @Prop({ default: 0 }) private min!: number;
  @Prop({ default: Number.MAX_SAFE_INTEGER }) private max!: number;
  @Prop({ default: false }) private showBeforeMin!: boolean;
  @Prop({ default: false }) private showBeyondMax!: boolean;

  @Provide() private graphId = generateUuid("line-graph-xxxx");

  @Watch("data")
  onDataChanged(newData: LineGraphData[]) {
    this.plotGraphFromData(newData, this.graphOptions());
  }

  mounted(): void {
    this.plotGraphFromData(this.data, this.graphOptions());
    window.addEventListener("resize", this.handleScreenResize);
  }

  destroyed() {
    window.removeEventListener("resize", this.handleScreenResize);
  }

  handleScreenResize() {
    this.plotGraphFromData(this.data, this.graphOptions());
  }

  graphOptions<LineGraphOptions>() {
    return {
      min: this.min,
      max: this.max,
      showBeforeMin: this.showBeforeMin,
      showBeyondMax: this.showBeyondMax,
      beforeMinStyle: "",
      beyondMaxStyle: ""
    };
  }

  plotGraphFromData(
    data: LineGraphData[],
    options: LineGraphOptions = {
      min: 0,
      max: Number.MAX_SAFE_INTEGER,
      showBeforeMin: false,
      showBeyondMax: false,
      beyondMaxStyle: "",
      beforeMinStyle: ""
    },
    margin = { top: 0, left: 60, bottom: 40, right: 25 }
  ) {
    const dataInRange = data.map(group => ({
      ...group,
      data: group.data.filter(
        datum => datum.date >= options.min && datum.date <= options.max
      )
    }));

    const dataBeforeMin = data.map(group => ({
      ...group,
      data: group.data.filter(
        datum => options.showBeforeMin && datum.date <= options.min
      )
    }));

    const dataBeyondMax = data.map(group => ({
      ...group,
      data: group.data.filter(
        datum => options.showBeyondMax && datum.date >= options.max
      )
    }));

    const fullRangeData = [...dataBeforeMin, ...dataInRange, ...dataBeyondMax];

    const container = this.$refs[this.graphId] as SVGElement;

    if (!container) {
      return;
    }

    const height = container.clientHeight - (margin.top + margin.bottom);
    const width = container.clientWidth - (margin.left + margin.right);
    const marginTransform = "translate(" + margin.left + "," + margin.top + ")";
    const svg = d3.select(container).html("");
    if (height < 1 || width < 1) {
      return;
    }
    svg.attr(
      "viewBox",
      "0 0 " + container.clientWidth + " " + container.clientHeight
    );
    svg.attr("preserveAspectRatio", "none");
    svg.append("g").attr("id", "background");

    const earliestDate = getMin(fullRangeData, group =>
      getMin(group.data, datum => datum.date)
    );
    const latestDate = getMax(fullRangeData, group =>
      getMax(group.data, datum => datum.date)
    );
    const x = d3
      .scaleLinear()
      .domain([earliestDate, latestDate])
      .range([0, width]);

    svg
      .append("g")
      .attr("class", "axis")
      .attr("transform", "translate(" + margin.left + "," + height + ")")
      .attr("vector-effect", "non-scaling-stroke")
      .call(
        d3
          .axisBottom(x)
          .ticks(latestDate - earliestDate)
          .tickFormat(d3.format("d"))
      );

    // Add Y axis
    const y = d3
      .scaleLinear()
      .domain([
        0,
        getMax(fullRangeData, group => getMax(group.data, datum => datum.value))
      ])
      .range([height, 0]);
    svg
      .append("g")
      .attr("class", "axis")
      .attr("transform", marginTransform)
      .attr("vector-effect", "non-scaling-stroke")
      .call(
        d3
          .axisLeft(y)
          .tickSize(-width)
          .tickFormat(d3.format("d"))
      );

    const plotLines = (plotData: LineGraphData[], classSuffix?: string) => {
      const styleSuffix = classSuffix ? `-${classSuffix}` : "";
      plotData.forEach(group => {
        const { id, color, data: plotPoints } = group;

        const xCalculateFromDatum = (datum: any) =>
          x(((datum as unknown) as LineGraphPlotPoint).date);
        const yCalculateFromDatum = (datum: any) =>
          y(((datum as unknown) as LineGraphPlotPoint).value);

        const lines = (d3
          .line()
          .x(xCalculateFromDatum)
          .y(yCalculateFromDatum) as unknown) as string;

        const firstDatum = plotPoints[0];
        const lastDatum = plotPoints[plotPoints.length - 1];

        const graphBackgroundId = `graph-background${styleSuffix}`;
        if (
          firstDatum !== lastDatum &&
          svg.select(`#${graphBackgroundId}`).empty()
        ) {
          svg
            .select("#background")
            .append("rect")
            .attr("id", graphBackgroundId)
            .attr("class", graphBackgroundId)
            .attr("transform", marginTransform)
            .attr("x", xCalculateFromDatum(firstDatum))
            .attr("y", margin.top)
            .attr(
              "width",
              xCalculateFromDatum(lastDatum) - xCalculateFromDatum(firstDatum)
            )
            .attr("height", height);
        }

        svg
          .append("path")
          .datum(plotPoints)
          .attr("class", `graph-line${styleSuffix}`)
          .attr("transform", marginTransform)
          .attr("vector-effect", "non-scaling-stroke")
          .attr("stroke", color)
          .attr("d", lines);

        svg
          .selectAll(".circle")
          .data(plotPoints)
          .enter()
          .append("circle")
          .attr("cx", xCalculateFromDatum)
          .attr("cy", yCalculateFromDatum)
          .attr("class", `graph-node${styleSuffix}`)
          .attr("vector-effect", "non-scaling-stroke")
          .attr("fill", color)
          .attr("transform", marginTransform);
      });
    };
    plotLines(dataBeforeMin, "before-min");
    plotLines(dataBeyondMax, "beyond-max");
    plotLines(dataInRange);
  }
}
